import axios, { AxiosRequestConfig, isAxiosError } from "axios"

import URLS from "~/constants/urls"
import useAgentOsStore from "~/store/agent-os"

type RetryFetchOptions = RequestInit & {
  retries?: number
  retryDelay?: number
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const retryFetch = async (
  url: string,
  options: RetryFetchOptions = {}
): Promise<Response> => {
  const { retries = 3, retryDelay = 1000, ...fetchOptions } = options
  const method = fetchOptions.method?.toUpperCase() ?? "GET"

  const shouldRetry = method === "GET" || method === "POST"

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, fetchOptions)

      if (shouldRetry && response.status >= 500 && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      return response
    } catch (error) {
      lastError = error as Error

      if (shouldRetry && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      throw error
    }
  }

  throw lastError || new Error("Fetch failed after retries")
}

export const axiosInstance = axios.create({
  baseURL: URLS.V1,
  headers: {
    "Content-Type": "application/json"
  }
})

// PROTOTYPE prospectOS (RC3): endpoints whose 401 must NOT tear down the
// session. The outbound/prospect flow fans out many data calls — account
// people search, CRM pipeline recommendations, contact enrichment, and the 3s
// run poller. A single 401 from any of them used to clearStorage + POST
// /logout + redirect, logging the user out mid-flow (often correlated with
// long, high-latency runs where one call transiently 401s). Treat these as
// soft failures: reject so the caller handles it, but keep the session alive.
const SOFT_401_URL_FRAGMENTS = [
  "agent-workflows",
  "agent-os",
  // Executive Profile lives under /exec-profile/ rather than /agent-os/, so it
  // did not match anything above. A 401 from it cleared storage and bounced the
  // user to /login in the middle of the conversation, which reads as the page
  // reloading itself for no reason.
  "exec-profile",
  "people_search",
  "peopleSearch",
  "additionalPeople",
  "email_enrichment",
  "inside_sales",
  "hot_accounts"
]

const is401SoftEndpoint = (url?: string) => {
  if (!url) return false
  return SOFT_401_URL_FRAGMENTS.some((frag) => url.includes(frag))
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? ""
      // Per-request opt-out (config.skipAuthRedirect) OR a known background/
      // agent data endpoint: fail soft without destroying the session, so a
      // transient poller 401 during a long run doesn't log the user out
      // mid-flow. A genuinely dead session still 401s on non-soft endpoints
      // (app shell, other APIs) and triggers the full logout there.
      const skipRedirect =
        (error.config as any)?.skipAuthRedirect === true ||
        is401SoftEndpoint(url)
      // Inside an iframe, never clobber the shared parent session.
      const inIframe =
        typeof window !== "undefined" && window.top !== window.self
      if (inIframe || skipRedirect) {
        return Promise.reject(error)
      }
      useAgentOsStore.persist.clearStorage()
      await fetch("/logout", { method: "POST" })
      window.location.href = "/login"
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export const fetchDataOnServer = async (
  options: AxiosRequestConfig,
  request: Request,
  { skipAuthRedirect = false }: { skipAuthRedirect?: boolean } = {}
) => {
  try {
    const response = await axios({
      ...options,
      headers: {
        ...options.headers
      }
    })
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401 && !skipAuthRedirect) {
        const { logout } = await import("~/session")
        throw await logout(request)
      } else if (
        error.response?.status === 400 ||
        error.response?.status === 401
      ) {
        return {
          error: error.response.data?.error?.message,
          status: error.response?.status
        }
      } else {
        return {
          error: error.response?.data?.error?.message,
          status: error.response?.status
        }
      }
    }
    throw error
  }
}
